import React, { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { cn } from '@/utils/cn';
import {
  AgentFormValues,
  PromptCategory,
  PROMPT_CATEGORIES,
  PROMPT_VARIABLES,
  PROMPT_VARIABLE_GROUPS,
  TENANT_ROLES,
  screenMeta,
  areaCls,
  UNREACHABLE_MODULES,
  SCREEN_META,
} from './agentShared';
import { Button } from '@/components/UI/Button';
import { fieldClass } from '@/components/UI/fieldStyles';
import { usePromptBlockCheck } from './usePromptChecks';

// ── RolePicker (inline panel) ──────────────────────────────────────────────────

/** The persona/rules/context tag picker is hidden in the UI; stored categories are kept as they are. */
const SHOW_CATEGORY_TAG = false;

const RolePicker: React.FC<{
  selected: string[];
  onChange: (roles: string[]) => void;
  onDone: () => void;
}> = ({ selected, onChange, onDone }) => {
  const toggle = (role: string) => {
    onChange(selected.includes(role) ? selected.filter((r) => r !== role) : [...selected, role]);
  };

  return (
    <div className="border-b border-ui-border bg-ui-card px-4 py-3">
      <p className="mb-2 text-xs text-ui-text3">
        Block is injected only for the selected roles. Leave empty to inject for everyone.
      </p>
      <div className="flex flex-wrap gap-2">
        {TENANT_ROLES.map((role) => {
          const checked = selected.includes(role.value);
          return (
            <label
              key={role.value}
              className={cn(
                'inline-flex cursor-pointer select-none items-center gap-1.5 rounded-chip border px-2.5 py-1 text-xs transition-colors',
                checked
                  ? 'border-accent/30 bg-accent/5 font-medium text-ui-text'
                  : 'border-ui-border bg-ui-card text-ui-text2 hover:bg-ui-hover',
              )}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(role.value)}
                className="h-3 w-3 accent-accent"
              />
              {role.label}
            </label>
          );
        })}
      </div>
      <div className="mt-2.5 flex items-center justify-between">
        {selected.length > 0 ? (
          <Button type="button" variant="link" size="xs" className="text-danger-text" onClick={() => onChange([])}>
            Clear — always inject
          </Button>
        ) : (
          <span className="text-xs text-ui-text3">No restriction — always injected</span>
        )}
        <Button type="button" variant="link" size="xs" className="text-accent-text" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
};

// ── ScreenPicker (inline panel) ─────────────────────────────────────────────────

const ScreenPicker: React.FC<{
  selected: string[];
  onChange: (modules: string[]) => void;
  onDone: () => void;
}> = ({ selected, onChange, onDone }) => {
  const ALL_SCREENS = Object.keys(SCREEN_META).filter((m) => !UNREACHABLE_MODULES.has(m));
  const toggle = (module: string) => {
    onChange(selected.includes(module) ? selected.filter((m) => m !== module) : [...selected, module]);
  };

  return (
    <div className="border-b border-ui-border bg-ui-card px-4 py-3">
      <p className="mb-2 text-xs text-ui-text3">
        Block is injected only while the selected screen(s) are open. Leave empty to inject on every screen.
      </p>
        <div className="flex flex-wrap gap-2">
          {ALL_SCREENS.map((module) => {
            const checked = selected.includes(module);
            const meta = screenMeta(module);
            return (
              <label
                key={module}
                title={meta.description}
                className={cn(
                  'inline-flex cursor-pointer select-none items-center gap-1.5 rounded-chip border px-2.5 py-1 text-xs transition-colors',
                  checked
                    ? 'border-accent/30 bg-accent/5 font-medium text-ui-text'
                    : 'border-ui-border bg-ui-card text-ui-text2 hover:bg-ui-hover',
                )}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(module)}
                  className="h-3 w-3 accent-accent"
                />
                {meta.label}
              </label>
            );
          })}
        </div>
      <div className="mt-2.5 flex items-center justify-between">
        {selected.length > 0 ? (
          <Button type="button" variant="link" size="xs" className="text-danger-text" onClick={() => onChange([])}>
            Clear — always inject
          </Button>
        ) : (
          <span className="text-xs text-ui-text3">No restriction — always injected</span>
        )}
        <Button type="button" variant="link" size="xs" className="text-accent-text" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
};

// ── PromptBlock ────────────────────────────────────────────────────────────────

interface PromptBlockProps {
  index: number;
  total: number;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
  /** 1-based place of this block in the list the user sees. With `onMoveTo`, the number becomes editable and
   * the block can be dragged by its handle onto another block. */
  position?: number;
  onMoveTo?: (position: number) => void;
  /** Show the "+ screens" module-scoping affordance. Default true (Instructions tab). The Modules
   * tab passes false — a block edited from a screen's own card already has its module fixed by
   * context (home_module or topic_modules, set by the caller), so re-scoping it from here would be
   * redundant and confusing. Every other part of the editor (tag, roles, variable chips) is
   * identical either way. */
  showScreenPicker?: boolean;
  /** Extra controls shown between the header and the prompt text (the Cards tab puts the block's condition and tools here). */
  slot?: React.ReactNode;
}

const PromptBlock: React.FC<PromptBlockProps> = ({ index, total, onMove, onRemove, position, onMoveTo, showScreenPicker: allowScreenPicker = true, slot }) => {
  const [dragOver, setDragOver] = useState(false);
  const check = usePromptBlockCheck(index);
  const issues = check?.errors ?? [];
  const [showSample, setShowSample] = useState(false);
  const [showVars, setShowVars] = useState(false);       // the merge-variable chips start folded
  const [posDraft, setPosDraft] = useState<string | null>(null);
  const reorderable = position !== undefined && !!onMoveTo;
  const commitPos = () => {
    const n = Math.round(Number(posDraft));
    setPosDraft(null);
    if (onMoveTo && posDraft !== null && Number.isFinite(n) && n >= 1 && n <= total && n !== position) onMoveTo(n);
  };
  const { register, watch, setValue } = useFormContext<AgentFormValues>();
  const [showCatPicker, setShowCatPicker] = useState(false);
  const [showRolePicker, setShowRolePicker] = useState(false);
  const [showScreenPicker, setShowScreenPicker] = useState(false);
  const [customTag, setCustomTag] = useState('');

  const category = watch(`system_prompts.${index}.category`) as PromptCategory | undefined;
  const conditionRoles = (watch(`system_prompts.${index}.condition_roles`) as string[] | undefined) ?? [];
  const conditionModules = (watch(`system_prompts.${index}.condition_modules`) as string[] | undefined) ?? [];
  const catCfg = category ? PROMPT_CATEGORIES[category] : null;

  // ── variable insertion ─────────────────────────────────────────────────────

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Fit the box to the text: a short prompt gets a short box, a long one stops at 10 lines (scrolls; the
  // corner handle still resizes it by hand).
  const promptValue = watch(`system_prompts.${index}.prompt`) as string | undefined;
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    const lineH = parseFloat(getComputedStyle(el).lineHeight) || 22.75;
    const pad = el.offsetHeight - el.clientHeight + parseFloat(getComputedStyle(el).paddingTop) + parseFloat(getComputedStyle(el).paddingBottom);
    el.style.height = 'auto';
    const border = el.offsetHeight - el.clientHeight;
    el.style.height = `${Math.min(Math.max(el.scrollHeight + border, lineH * 3 + pad), lineH * 10 + pad)}px`;
  }, [promptValue]);
  const { ref: rhfRef, onChange, onBlur, name } = register(`system_prompts.${index}.prompt`);

  const combineRef = (el: HTMLTextAreaElement | null) => {
    textareaRef.current = el;
    if (typeof rhfRef === 'function') {
      rhfRef(el);
    } else if (rhfRef) {
      (rhfRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
    }
  };

  const insertVariable = (token: string) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? start;
    const snippet = `{{ ${token} }}`;
    const newValue = el.value.slice(0, start) + snippet + el.value.slice(end);
    setValue(`system_prompts.${index}.prompt`, newValue, { shouldDirty: true });
    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      const pos = start + snippet.length;
      textareaRef.current.setSelectionRange(pos, pos);
      textareaRef.current.focus();
    });
  };

  // ── setters ────────────────────────────────────────────────────────────────

  const setCategory = (cat: PromptCategory | undefined) => {
    setValue(`system_prompts.${index}.category`, cat as never, { shouldDirty: true });
    setShowCatPicker(false);
  };

  const setRoles = (roles: string[]) => {
    setValue(`system_prompts.${index}.condition_roles`, roles as never, { shouldDirty: true });
  };

  const setModules = (modules: string[]) => {
    setValue(`system_prompts.${index}.condition_modules`, modules as never, { shouldDirty: true });
  };

  const toggleCatPicker = () => { setShowCatPicker((p) => !p); setShowRolePicker(false); setShowScreenPicker(false); };
  const toggleRolePicker = () => { setShowRolePicker((p) => !p); setShowCatPicker(false); setShowScreenPicker(false); };
  const toggleScreenPicker = () => { setShowScreenPicker((p) => !p); setShowCatPicker(false); setShowRolePicker(false); };

  // ── role / screen badge labels ────────────────────────────────────────────

  const roleBadgeLabel =
    conditionRoles.length > 0
      ? conditionRoles
          .map((r) => TENANT_ROLES.find((t) => t.value === r)?.label ?? r)
          .join(', ')
      : null;

  const screenBadgeLabel =
    conditionModules.length > 0
      ? conditionModules.map((m) => screenMeta(m).label).join(', ')
      : null;

  return (
    <div
      className={cn('overflow-hidden rounded-card border', dragOver ? 'border-accent ring-2 ring-ui-ring/40' : 'border-ui-border')}
      onDragOver={reorderable ? (e) => { if (e.dataTransfer.types.includes('application/x-prompt-block')) { e.preventDefault(); setDragOver(true); } } : undefined}
      onDragLeave={reorderable ? () => setDragOver(false) : undefined}
      onDrop={reorderable ? (e) => {
        setDragOver(false);
        const from = Number(e.dataTransfer.getData('application/x-prompt-block'));
        if (Number.isFinite(from) && from >= 1 && from !== position) { e.preventDefault(); onMoveTo?.(position!); }
        else if (from === position) e.preventDefault();
      } : undefined}
    >

      {/* ── header ── */}
      <div className="flex items-center gap-2 border-b border-ui-border bg-ui-subtle px-3 py-2.5">
        {/* order: drag handle, position number (type a number to jump there), step up / down */}
        {reorderable && (
          <span
            draggable
            onDragStart={(e) => { e.dataTransfer.setData('application/x-prompt-block', String(position)); e.dataTransfer.effectAllowed = 'move'; }}
            title="Drag onto another block to move this one there"
            aria-label={`Drag to reorder block ${position}`}
            className="shrink-0 cursor-grab select-none px-0.5 text-base leading-none text-ui-text3 hover:text-ui-text2 active:cursor-grabbing"
          >⠿</span>
        )}
        {reorderable && (
          <input
            type="number" min={1} max={total} inputMode="numeric"
            aria-label={`Position of block ${position} of ${total}`}
            title="Type a number to move this block to that position"
            value={posDraft ?? String(position)}
            onChange={(e) => setPosDraft(e.target.value)}
            onFocus={(e) => e.currentTarget.select()}
            onBlur={commitPos}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setPosDraft(null); e.currentTarget.blur(); } }}
            className="h-6 w-9 shrink-0 rounded-full border border-ui-border bg-ui-card text-center text-xs font-semibold text-ui-text [appearance:textfield] focus:outline-none focus:ring-2 focus:ring-ui-ring [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
        )}
        <div className="flex shrink-0 flex-col gap-0.5">
          <button type="button" onClick={() => onMove(-1)} disabled={reorderable ? position === 1 : index === 0}
            title="Move up" aria-label="Move block up"
            className="rounded px-1 text-xs leading-none text-ui-text3 hover:bg-ui-hover hover:text-ui-text focus-ring disabled:opacity-25">▲</button>
          <button type="button" onClick={() => onMove(1)} disabled={reorderable ? position === total : index === total - 1}
            title="Move down" aria-label="Move block down"
            className="rounded px-1 text-xs leading-none text-ui-text3 hover:bg-ui-hover hover:text-ui-text focus-ring disabled:opacity-25">▼</button>
        </div>

        {/* category chip — hidden for now (set SHOW_CATEGORY_TAG to bring it back) */}
        {SHOW_CATEGORY_TAG && (
          <button
            type="button"
            onClick={toggleCatPicker}
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium transition',
              catCfg
                ? catCfg.chip
                : category
                ? 'border-ui-border bg-ui-subtle text-ui-text2'
                : 'border-ui-border bg-ui-card text-ui-text3 hover:border-ui-border',
            )}
          >
            {catCfg && <span className={cn('h-1.5 w-1.5 rounded-full', catCfg.dot)} />}
            {catCfg ? catCfg.label : category ? category : '+ tag'}
            <span className="text-xs opacity-50">▾</span>
          </button>
        )}

        {/* role condition badge */}
        {roleBadgeLabel ? (
          <button
            type="button"
            onClick={toggleRolePicker}
            className="inline-flex shrink-0 items-center gap-1 rounded-full border border-primary/30 bg-primary-soft px-2 py-0.5 text-xs font-medium text-primary-text transition-colors hover:brightness-95"
          >
            <span className="text-xs">◎</span>
            {roleBadgeLabel}
          </button>
        ) : null}

        {/* screen condition badge — legacy condition_modules only; hidden when the caller already
            fixes the module by context (home_module/topic_modules set from the Modules tab) */}
        {allowScreenPicker && (screenBadgeLabel ? (
          <button
            type="button"
            onClick={toggleScreenPicker}
            className="inline-flex shrink-0 items-center gap-1 rounded-full border border-accent/30 bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent-text transition hover:bg-accent/15"
          >
            <span className="text-xs">▣</span>
            {screenBadgeLabel}
          </button>
        ) : null)}

        {/* title — borderless fill */}
        <input
          className="min-w-0 flex-1 bg-transparent text-sm text-ui-text placeholder:text-ui-text3 focus:outline-none"
          placeholder="Title (optional)"
          {...register(`system_prompts.${index}.title`)}
        />

        {/* remove */}
        <Button type="button" variant="destructive-ghost" size="xs" className="shrink-0" onClick={onRemove}>
          Remove
        </Button>
      </div>

      {/* ── category picker ── */}
      {showCatPicker && (
        <div className="flex flex-wrap items-center gap-1.5 border-b border-ui-border bg-ui-card px-3 py-2.5">
          {(Object.entries(PROMPT_CATEGORIES) as [PromptCategory, typeof PROMPT_CATEGORIES[PromptCategory]][]).map(
            ([key, cfg]) => (
              <button
                key={key}
                type="button"
                onClick={() => setCategory(category === key ? undefined : key)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition',
                  category === key ? cfg.chip : 'border-ui-border bg-ui-card text-ui-text2 hover:bg-ui-hover',
                )}
              >
                <span className={cn('h-1.5 w-1.5 rounded-full', cfg.dot)} />
                {cfg.label}
              </button>
            ),
          )}
          {category && (
            <button type="button" onClick={() => setCategory(undefined)}
              className="ml-1 text-xs text-ui-text3 hover:text-ui-text2">
              Clear
            </button>
          )}

          {/* Custom tag */}
          <div className="ml-auto flex items-center gap-1">
            <input
              value={customTag}
              onChange={(e) => setCustomTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const val = customTag.trim().toLowerCase().replace(/\s+/g, '_');
                  if (val) { setCategory(val as PromptCategory); setCustomTag(''); }
                }
              }}
              placeholder="custom tag…"
              className={cn(fieldClass({ size: 'xs' }), 'w-28')}
            />
            <Button
              type="button"
              variant="link"
              size="xs"
              className="text-accent-text"
              onClick={() => {
                const val = customTag.trim().toLowerCase().replace(/\s+/g, '_');
                if (val) { setCategory(val as PromptCategory); setCustomTag(''); }
              }}
            >
              Set
            </Button>
          </div>
        </div>
      )}

      {/* ── role picker ── */}
      {showRolePicker && (
        <RolePicker
          selected={conditionRoles}
          onChange={setRoles}
          onDone={() => setShowRolePicker(false)}
        />
      )}

      {/* ── screen picker ── */}
      {allowScreenPicker && showScreenPicker && (
        <ScreenPicker
          selected={conditionModules}
          onChange={setModules}
          onDone={() => setShowScreenPicker(false)}
        />
      )}

      {slot}

      {/* ── prompt body ── */}
      <div className="bg-ui-card p-3">
        <textarea
          ref={combineRef}
          name={name}
          onChange={onChange}
          onBlur={onBlur}
          rows={3}
          placeholder="Prompt content"
          aria-invalid={issues.length > 0 || undefined}
          className={cn(areaCls, 'resize-y font-mono text-sm leading-relaxed', issues.length > 0 && 'border-danger focus:border-danger')}
        />
        {issues.length > 0 && (
          <ul role="alert" className="mt-2 space-y-1 rounded-control border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger-text">
            {issues.map((e, i) => (
              <li key={i}>
                <span className="font-semibold">{e.line ? `Line ${e.line}: ` : ''}</span>{e.message}
              </li>
            ))}
            <li className="text-ui-text3">Fix the template; the model would otherwise receive this block with the mistake in it.</li>
          </ul>
        )}
        {check?.ok && check.rendered ? (
          <div className="mt-2">
            <button type="button" onClick={() => setShowSample((v) => !v)} aria-expanded={showSample}
              className="inline-flex items-center gap-1.5 rounded text-xs font-medium text-ui-text3 transition hover:text-ui-text2 focus-ring">
              <span className={cn('inline-block transition-transform', showSample && 'rotate-90')}>▸</span>
              <span className="text-success-text">✓ Template OK</span> — preview with sample data
            </button>
            {showSample && (
              <pre className="mt-1.5 max-h-60 overflow-auto whitespace-pre-wrap rounded-control border border-ui-border bg-ui-subtle p-3 font-mono text-xs text-ui-text2">{check.rendered}</pre>
            )}
          </div>
        ) : null}

        {/* variable chips — grouped by User / Tenant / Session */}
        <button
          type="button"
          onClick={() => setShowVars((v) => !v)}
          aria-expanded={showVars}
          className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-medium text-ui-text3 transition hover:text-ui-text2 focus-ring rounded"
        >
          <span className={cn('inline-block transition-transform', showVars && 'rotate-90')}>▸</span>
          Insert a variable ({PROMPT_VARIABLES.length})
        </button>
        {showVars && (
        <div className="mt-2 space-y-1.5">
          {PROMPT_VARIABLE_GROUPS.map(({ id: group, title }) => {
            const vars = PROMPT_VARIABLES.filter((v) => v.group === group);
            return (
              <div key={group} className="flex flex-wrap items-center gap-1.5">
                <span className="w-24 shrink-0 text-xs font-medium text-ui-text3">{title}</span>
                {vars.map((v) => (
                  <button
                    key={v.token}
                    type="button"
                    onClick={() => insertVariable(v.token)}
                    title={`${v.label}${v.hint ? ` — ${v.hint}` : ''}\nInserts {{ ${v.token} }}`}
                    className="rounded-chip border border-ui-border bg-ui-subtle px-2 py-0.5 text-xs text-ui-text2 transition-colors hover:border-accent/40 hover:bg-accent/10 hover:text-accent-text"
                  >
                    {v.short}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
        )}
      </div>
    </div>
  );
};

export default PromptBlock;
